/**
 * The backend's error envelope, and how to read it.
 *
 * Every failure from the API — 401 through 500 — has the same shape:
 *
 * ```json
 * {"error": {"type": "resource_not_found", "message": "...", "request_id": "..."}}
 * ```
 *
 * so the UI needs exactly one parser. Branch on `type`, show `message`, and
 * quote `requestId` in a bug report: it is the same id the backend logged.
 */
export type ApiErrorBody = {
  error: {
    type: string
    message: string
    request_id?: string
    fields?: Array<{ field: string; reason: string }>
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false
  const { error } = value
  return typeof error === 'object' && error !== null && 'message' in error
}

/** A human-readable message for anything thrown by a query or a mutation. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (isApiErrorBody(error)) return error.error.message
  if (error instanceof Error && error.message) return error.message
  return fallback
}

/** The backend's machine-readable code, when there is one, for branching. */
export function errorType(error: unknown): string | undefined {
  return isApiErrorBody(error) ? error.error.type : undefined
}

/** The request id to quote in a bug report, when the backend sent one. */
export function errorRequestId(error: unknown): string | undefined {
  return isApiErrorBody(error) ? error.error.request_id : undefined
}

/** Per-field validation detail from a 422, for attaching to form inputs. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!isApiErrorBody(error) || !error.error.fields) return {}
  return Object.fromEntries(error.error.fields.map((f) => [f.field, f.reason]))
}

/**
 * A 403 from either service: signed in, and refused. Asking again will not
 * change the answer — asking somebody for access might. The backend's
 * envelope carries the status stamped on by `stampErrorStatus`; the auth
 * service's errors carry their own.
 */
export function isForbiddenError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === 403
}
